import { createFileRoute, Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { toast } from "sonner";
import { Sparkles, Loader2, Eye, EyeOff, ShieldCheck, Lock, BadgeCheck } from "lucide-react";
import { PeopelsLogo } from "@/components/brand/PeopelsLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";

const search = z.object({
  mode: z.enum(["signin", "signup"]).optional(),
  redirect: z.string().optional(),
  ref: z.string().trim().min(4).max(16).optional(),
}).partial();

export const Route = createFileRoute("/auth")({
  validateSearch: (s) => search.parse(s),
  head: () => ({
    meta: [
      { title: "Sign in — Peopels" },
      { name: "description", content: "Sign in or create your free Peopels account to post tasks or work as a Helper across Greater London." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AuthPage,
});

const signupSchema = z.object({
  full_name: z.string().trim().min(2, "Please enter your full name").max(80),
  email: z.string().trim().email("Enter a valid email address").max(255),
  password: z.string().min(8, "Use at least 8 characters").max(72),
});
const signinSchema = z.object({
  email: z.string().trim().email("Enter a valid email address"),
  password: z.string().min(1, "Enter your password"),
});

type FieldErrors = Partial<Record<"full_name" | "email" | "password" | "form", string>>;

function passwordStrength(pw: string): { score: 0 | 1 | 2 | 3 | 4; label: string } {
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
  if (/\d/.test(pw) && /[^A-Za-z0-9]/.test(pw)) score++;
  const labels = ["Too short", "Weak", "Okay", "Strong", "Excellent"] as const;
  return { score: Math.min(score, 4) as 0 | 1 | 2 | 3 | 4, label: labels[Math.min(score, 4)] };
}

// Open-redirect guard: only allow same-origin paths that start with a single "/"
// and not "//" (which the browser treats as protocol-relative to another host).
function safeRedirect(r: string | undefined): string {
  if (!r) return "/dashboard";
  if (!/^\/(?!\/)[A-Za-z0-9\-._~!$&'()*+,;=:@/%?#]*$/.test(r)) return "/dashboard";
  return r;
}

function AuthPage() {
  const { mode = "signin", redirect, ref } = useSearch({ from: "/auth" });
  const navigate = useNavigate();
  const isSignup = mode === "signup";
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<FieldErrors>({});
  const strength = useMemo(() => passwordStrength(password), [password]);
  const safeTarget = useMemo(() => safeRedirect(redirect), [redirect]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) navigate({ to: safeTarget });
    });
  }, [navigate, safeTarget]);

  // Funnel: auth view (re-fires when toggling mode so we can compare signin vs signup)
  useEffect(() => {
    void import("@/lib/funnel-track").then(({ track }) =>
      track("auth_view", { mode: isSignup ? "signup" : "signin", redirect: redirect ?? null }),
    );
  }, [isSignup, redirect]);

  // Reset password & errors when toggling modes
  useEffect(() => { setErrors({}); setPassword(""); }, [isSignup]);


  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const payload = {
      full_name: String(fd.get("full_name") ?? ""),
      email: String(fd.get("email") ?? ""),
      password: String(fd.get("password") ?? ""),
    };

    const result = isSignup ? signupSchema.safeParse(payload) : signinSchema.safeParse(payload);
    if (!result.success) {
      const fieldErrors: FieldErrors = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0] as keyof FieldErrors;
        if (key && !fieldErrors[key]) fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }
    setErrors({});
    setLoading(true);
    try {
      if (isSignup) {
        const data = result.data as z.infer<typeof signupSchema>;
        const { error } = await supabase.auth.signUp({
          email: data.email,
          password: data.password,
          options: {
            emailRedirectTo: window.location.origin,
            data: {
              full_name: data.full_name,
              ...(ref ? { ref: ref.toUpperCase() } : {}),
            },
          },
        });
        if (error) {
          const msg = /registered|already/i.test(error.message)
            ? "An account with this email already exists. Try signing in instead."
            : error.message;
          setErrors({ form: msg });
          return;
        }
        toast.success("Welcome to Peopels!");
        void import("@/lib/funnel-track").then(({ track }) => track("auth_signup_success", { method: "email" }));
        navigate({ to: redirect ?? "/dashboard" });
      } else {
        const data = result.data as z.infer<typeof signinSchema>;
        const { error } = await supabase.auth.signInWithPassword(data);
        if (error) {
          const msg = /invalid/i.test(error.message)
            ? "Email or password is incorrect. Please try again."
            : error.message;
          setErrors({ form: msg });
          return;
        }
        toast.success("Signed in");
        void import("@/lib/funnel-track").then(({ track }) => track("auth_signin_success", { method: "email" }));
        navigate({ to: redirect ?? "/dashboard" });
      }

    } catch (err) {
      setErrors({ form: err instanceof Error ? err.message : "Something went wrong. Please try again." });
    } finally { setLoading(false); }
  };

  const handleGoogle = async () => {
    setOauthLoading(true);
    setErrors({});
    try {
      const result = await lovable.auth.signInWithOAuth("google", { redirect_uri: window.location.origin });
      if (result.error) { setErrors({ form: "Google sign-in failed. Please try again." }); return; }
      if (result.redirected) return;
      navigate({ to: redirect ?? "/dashboard" });
    } finally { setOauthLoading(false); }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="hidden bg-gradient-hero p-12 text-primary-foreground lg:flex lg:flex-col lg:justify-between">
        <Link to="/" aria-label="Peopels home" className="inline-flex items-center">
          <img src="/android-chrome-512x512.png" alt="Peopels" width={140} height={140} className="h-24 w-24 rounded-2xl bg-white/10 p-3 backdrop-blur" />
        </Link>
        <div className="space-y-6">
          <h2 className="font-display text-4xl font-bold leading-tight">A quality-first way to get things done in London.</h2>
          <p className="max-w-md text-white/80">A new marketplace for trusted local services across every Greater London postcode. Built on verification, transparent fees, and honest reviews.</p>
          <ul className="space-y-2 text-sm text-white/85">
            <li className="flex items-center gap-2"><BadgeCheck className="h-4 w-4 text-emerald-300" /> ID & DBS-verified Helpers</li>
            <li className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-300" /> Payments held in escrow</li>
            <li className="flex items-center gap-2"><Lock className="h-4 w-4 text-emerald-300" /> Bank-grade encryption</li>
          </ul>
        </div>
        <p className="text-sm text-white/60">© {new Date().getFullYear()} Peopels Ltd</p>
      </div>

      <div className="flex items-center justify-center px-4 py-12 sm:px-6">
        <div className="w-full max-w-md">
          <Link to="/" aria-label="Peopels home" className="mb-8 inline-flex items-center lg:hidden">
            <PeopelsLogo variant="primary" height={56} />
          </Link>
          <h1 className="font-display text-3xl font-bold">{isSignup ? "Create your account" : "Welcome back"}</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {isSignup ? "Free to join. Takes under 30 seconds." : "Sign in to continue to Peopels."}
          </p>

          <Button onClick={handleGoogle} variant="outline" size="lg" className="mt-6 w-full" disabled={oauthLoading || loading}>
            {oauthLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <GoogleIcon />}
            Continue with Google
          </Button>

          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs uppercase tracking-wider text-muted-foreground">or with email</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          {errors.form && (
            <div role="alert" className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {errors.form}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {isSignup && (
              <div className="space-y-1.5">
                <Label htmlFor="full_name">Full name</Label>
                <Input
                  id="full_name" name="full_name" required autoComplete="name"
                  aria-invalid={!!errors.full_name}
                  aria-describedby={errors.full_name ? "full_name-err" : undefined}
                  className={errors.full_name ? "border-destructive" : undefined}
                />
                {errors.full_name && <p id="full_name-err" role="alert" className="text-xs text-destructive">{errors.full_name}</p>}
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email" name="email" type="email" required autoComplete="email" inputMode="email"
                aria-invalid={!!errors.email}
                aria-describedby={errors.email ? "email-err" : undefined}
                className={errors.email ? "border-destructive" : undefined}
              />
              {errors.email && <p id="email-err" role="alert" className="text-xs text-destructive">{errors.email}</p>}
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                {!isSignup && <Link to="/auth/reset" className="text-xs text-primary hover:underline">Forgot?</Link>}
              </div>
              <div className="relative">
                <Input
                  id="password" name="password" type={showPw ? "text" : "password"} required
                  autoComplete={isSignup ? "new-password" : "current-password"}
                  minLength={isSignup ? 8 : undefined}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  aria-invalid={!!errors.password}
                  aria-describedby={errors.password ? "password-err" : isSignup ? "password-hint" : undefined}
                  className={`pr-10 ${errors.password ? "border-destructive" : ""}`}
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute inset-y-0 right-0 grid w-10 place-items-center text-muted-foreground hover:text-foreground"
                  aria-label={showPw ? "Hide password" : "Show password"}
                  aria-pressed={showPw}
                  tabIndex={-1}
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.password && <p id="password-err" role="alert" className="text-xs text-destructive">{errors.password}</p>}
              {isSignup && !errors.password && (
                <div id="password-hint" className="space-y-1">
                  <div className="flex gap-1" aria-hidden>
                    {[0, 1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className={`h-1 flex-1 rounded-full transition-colors ${
                          i < strength.score
                            ? strength.score <= 1 ? "bg-destructive"
                              : strength.score === 2 ? "bg-amber-500"
                              : strength.score === 3 ? "bg-emerald-500"
                              : "bg-emerald-600"
                            : "bg-muted"
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {password ? `Strength: ${strength.label}` : "At least 8 characters. Mix letters, numbers & symbols."}
                  </p>
                </div>
              )}
            </div>
            <Button type="submit" size="lg" className="w-full" disabled={loading || oauthLoading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {loading ? (isSignup ? "Creating account…" : "Signing in…") : isSignup ? "Create account" : "Sign in"}
            </Button>
          </form>

          <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
            <Lock className="h-3 w-3" /> Secured with 256-bit SSL. We never share your details.
          </p>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            {isSignup ? "Already have an account? " : "New to Peopels? "}
            <Link to="/auth" search={{ mode: isSignup ? "signin" : "signup" }} className="font-medium text-primary hover:underline">
              {isSignup ? "Sign in" : "Create one"}
            </Link>
          </p>

          {isSignup && (
            <p className="mt-4 text-center text-xs text-muted-foreground">
              By creating an account you agree to our <Link to="/terms" className="underline">Terms</Link> and <Link to="/privacy" className="underline">Privacy Policy</Link>.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" aria-hidden>
      <path fill="#EA4335" d="M12 10.2v3.95h5.5c-.24 1.4-1.7 4.12-5.5 4.12-3.3 0-6-2.74-6-6.12s2.7-6.12 6-6.12c1.88 0 3.13.8 3.85 1.49l2.62-2.52C16.94 3.6 14.7 2.6 12 2.6 6.96 2.6 2.9 6.66 2.9 12s4.06 9.4 9.1 9.4c5.27 0 8.74-3.7 8.74-8.9 0-.6-.07-1.06-.16-1.5H12z"/>
    </svg>
  );
}
